import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useState, useEffect } from "react";
import { t } from "i18next";
import DataTable from "react-data-table-component";
import { Grid, Card, Backdrop } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import Loader from "components/loader";
import AlertMessage from "components/UI/AlertMessage";
import useAlert from "hooks/useAlert";
import useAPI from "hooks/useAPI";
import useConstants from "constants";
import { usePermissions } from "context/PermissionsContext";
import Unauthorized from "components/UI/Unauthorized";
import { TableHeading, TableCodeCell } from "components/TableComponents";
import SortIcon from "components/SortIcon";
import MBDrawer from "components/MBDrawer";
import PageSettings from "components/PageSettings";
import { Refresh, SettingsOutlined } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import SoftButton from "components/SoftButton";

const ControllerProperties = () => {
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();
  const [data, setData] = useState([]);
  const { per_page, items_per_page_options } = useConstants();

  const controller_properties_table_id = "controller_properties";
  const default_sort_column = "id";
  const default_sort_direction = "desc";
  const default_items_per_page = per_page;

  const [searchLoaded, setSearchLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(default_items_per_page);
  const [search, setSearch] = useState("");
  const { hasPermission } = usePermissions();
  const [backdrop, setBackdrop] = useState(false);
  const [orderColumn, setOrderColumn] = useState(default_sort_column);
  const [orderDirection, setOrderDirection] = useState(default_sort_direction);
  const [defaultSortColumnFromSettings, setDefaultSortColumnFromSettings] =
    useState(default_sort_column);
  const [defaultSortDirectionFromSettings, setDefaultSortDirectionFromSettings] =
    useState(default_sort_direction);
  const [defaultItemsPerPageFromSettings, setDefaultItemsPerPageFromSettings] =
    useState(default_items_per_page);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);
  const [settingsSaveBtnLoading, setSettingsSaveBtnLoading] = useState(false);
  const [tableId, setTableId] = useState(null);

  if (!hasPermission("can_view_controller")) {
    return <Unauthorized />;
  }

  const fetchData = async (
    sortCol = null,
    sortDir = null,
    pageSize = null,
    isReset = false,
    searchValue = null,
    nextPage = null
  ) => {
    setLoading(true);
    let requestConfig = {};
    if (!isInitialLoad && !isReset) {
      const params = {
        page: nextPage || page,
        per_page: pageSize || perPage,
        search: searchValue,
        order_col: sortCol || orderColumn,
        order_dir: sortDir || orderDirection,
      };
      requestConfig = { params };
    }
    try {
      const response = await api.get(`/controller-properties/`, requestConfig);
      if (response.status !== 200) {
        showAlert("error", response.data.error);
        setLoading(false);
        return;
      }
      const result = response.data;
      const page_settings = result.page_settings;
      setData(result.data);
      setTotalRows(result.recordsTotal);

      if (isInitialLoad || isReset) {
        if (page_settings && page_settings.sortColumn) {
          setOrderColumn(page_settings.sortColumn);
          setDefaultSortColumnFromSettings(page_settings.sortColumn);
        }
        if (page_settings && page_settings.sortDirection) {
          setOrderDirection(page_settings.sortDirection);
          setDefaultSortDirectionFromSettings(page_settings.sortDirection);
        }
        if (page_settings && page_settings.itemsPerPage) {
          setPerPage(page_settings.itemsPerPage);
          setDefaultItemsPerPageFromSettings(page_settings.itemsPerPage);
        }
        if (isReset && (!page_settings || Object.keys(page_settings).length === 0)) {
          setOrderColumn(default_sort_column);
          setOrderDirection(default_sort_direction);
          setPerPage(default_items_per_page);
          setDefaultSortColumnFromSettings(default_sort_column);
          setDefaultSortDirectionFromSettings(default_sort_direction);
          setDefaultItemsPerPageFromSettings(default_items_per_page);
        }
        setIsInitialLoad(false);
      }

      setLoading(false);
      setSearchLoaded(true);
    } catch (error) {
      showAlert("error", error?.response?.data?.error || "Failed to fetch data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(orderColumn, orderDirection, perPage, false, search, page);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(orderColumn, orderDirection, perPage, false, search, newPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1); // Reset to the first page
    fetchData(orderColumn, orderDirection, newPerPage, false, search, page);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1); // Reset to the first page
    fetchData(orderColumn, orderDirection, perPage, false, event.target.value, page);
  };

  const handleSort = (column, sortDirection) => {
    const newSortColumn = column.columnKey;
    let newSortDirection = sortDirection;
    if (newSortColumn === orderColumn) {
      newSortDirection = orderDirection === "asc" ? "desc" : "asc";
    }
    setOrderColumn(newSortColumn);
    setOrderDirection(newSortDirection);
    fetchData(newSortColumn, newSortDirection, perPage, false, search, page);
  };

  const handleSettingsClick = (tableId) => {
    setTableId(tableId);
    setOpenSettingsDrawer(true);
  };

  const handleSettingsChange = async (settings) => {
    const newSettings = settings[tableId];
    setSettingsSaveBtnLoading(true);
    try {
      const response = await api.post(`/user-page-settings/`, {
        page_slug: "controller-properties",
        settings: settings,
      });
      if (response.status == 200) {
        // Apply the new settings
        setOrderColumn(newSettings.sortColumn);
        setOrderDirection(newSettings.sortDirection);
        setPerPage(newSettings.itemsPerPage);
        setDefaultSortColumnFromSettings(newSettings.sortColumn);
        setDefaultSortDirectionFromSettings(newSettings.sortDirection);
        setDefaultItemsPerPageFromSettings(newSettings.itemsPerPage);
        setPage(1); // Reset to first page

        // Fetch data with new settings
        fetchData(
          newSettings.sortColumn,
          newSettings.sortDirection,
          newSettings.itemsPerPage,
          false,
          search,
          1
        );
        setOpenSettingsDrawer(false);
        setTableId(null);

        showAlert("success", response.data.message);
      } else {
        showAlert("error", response.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setSettingsSaveBtnLoading(false);
    }
  };

  const handleResetTable = () => {
    setSearch("");
    fetchData(orderColumn, orderDirection, perPage, true, "", 1);
  };

  const getSortIcon = (columnKey) => {
    const isActive = orderColumn === columnKey;
    return <SortIcon orderDirection={orderDirection} isActive={isActive} />;
  };

  const columns = [
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.hash_id")} />
          {getSortIcon("id")}
        </SoftBox>
      ),
      columnKey: "id",
      selector: (row) => row.id,
      sortable: true,
      width: "7%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.type")} />
          {getSortIcon("controller_type__name")}
        </SoftBox>
      ),
      columnKey: "controller_type__name",
      selector: (row) => row.controller_type,
      sortable: true,
      width: "15%",
    },
    // {
    //   name: <TableHeading text={t("lang.slug")} />,
    //   columnKey: "slug",
    //   selector: (row) => <TableCodeCell text={row.slug} />,
    //   sortable: true,
    //   width: "20%",
    // },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.label")} />
          {getSortIcon("label")}
        </SoftBox>
      ),
      columnKey: "label",
      selector: (row) => row.label,
      sortable: true,
    },
  ];

  const pagesList = [
    {
      name: t("lang.controllers"),
      route: "/controllers",
    },
    {
      name: t("lang.controller_properties"),
      route: "",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />
      <SoftBox pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} position="relative">
                {searchLoaded && (
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      width: {
                        xs: "100%", // Full width on extra-small screens (mobile)
                        sm: "12rem", // Fixed width on small and up
                        md: "20rem",
                      },
                      ml: "auto",
                      mb: "1rem",
                    }}
                  >
                    <Tooltip title={t("lang.settings")} arrow placement="top">
                      <SoftButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleSettingsClick(controller_properties_table_id)}
                        size="small"
                        sx={{
                          padding: "0.45rem 1rem",
                          "& svg": {
                            fontSize: "24px !important",
                          },
                        }}
                      >
                        <SettingsOutlined color="secondary" />
                      </SoftButton>
                    </Tooltip>
                    <Tooltip title={t("lang.reset")} arrow placement="top">
                      <SoftButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleResetTable()}
                        size="small"
                        sx={{
                          padding: "0.45rem 1rem",
                          "& svg": {
                            fontSize: "24px !important",
                          },
                        }}
                      >
                        <Refresh color="secondary" />
                      </SoftButton>
                    </Tooltip>
                    <SoftBox width="12rem" ml="auto">
                      <SoftInput
                        placeholder={t("lang.actions.search")}
                        fullWidth
                        value={search}
                        onChange={handleSearchChange}
                      />
                    </SoftBox>
                  </SoftBox>
                )}
                <DataTable
                  className={controller_properties_table_id}
                  key={`${orderColumn}-${orderDirection}-${perPage}-${data.length}`}
                  columns={columns}
                  data={data}
                  progressPending={loading}
                  progressComponent={<Loader />}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangeRowsPerPage={handlePerPageChange}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  sortServer
                  paginationPerPage={perPage}
                  paginationRowsPerPageOptions={items_per_page_options}
                  paginationDefaultPage={page}
                  clearSelectedRows={true}
                  responsive={true}
                  selectableRows={false}
                  pointerOnHover={false}
                  fixedHeader={true}
                  persistTableHead={true}
                  sortIcon={() => {
                    return <></>;
                  }}
                  highlightOnHover
                />
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <AlertMessage {...errorState} />
      <MBDrawer
        open={openSettingsDrawer}
        onClose={() => setOpenSettingsDrawer(false)}
        title={t("lang.settings")}
        anchor="right"
        height="100vh"
        width="400px"
      >
        <PageSettings
          saveBtnLoading={settingsSaveBtnLoading}
          currentSortColumn={orderColumn}
          currentSortDirection={orderDirection}
          currentItemsPerPage={perPage}
          tableId={tableId}
          defaultSortColumn={defaultSortColumnFromSettings}
          defaultSortDirection={defaultSortDirectionFromSettings}
          defaultItemsPerPage={defaultItemsPerPageFromSettings}
          sortableColumns={[
            { value: "id", label: t("lang.id") },
            { value: "controller_type__name", label: t("lang.type") },
            { value: "label", label: t("lang.label") },
          ]}
          onSettingsChange={handleSettingsChange}
        />
      </MBDrawer>
    </DashboardLayout>
  );
};

export default ControllerProperties;
